import { useVerifiedOnboarding } from "../../../onboard-verify/verified-onboarding-context.ts";
import { useCallback, useState } from "react";
import { CircleCheck } from "lucide-react";
import { Card } from "../../../../shared/card/card.tsx";
import { WizardRailLayout } from "../../../onboarding-shared/wizard-rail-layout.tsx";
import { VerifyEmailOtpModal } from "../../../onboard-verify/modals/verify-email-otp-modal.tsx";
import { usePrejoinOtp } from "../../../onboard-verify/hooks/use-prejoin-otp.ts";
import { ApiError } from "../../../../../utils/api.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { useUsPreJoinSignup } from "./hooks/use-us-pre-join-signup.ts";
import { buildUsPreJoinSignupPayload } from "./mappers/us-pre-join.mapper.ts";
import type { UsContactValues, UsIdentityValues, UsPersonalValues } from "./schemas/us-pre-join.schema.ts";
import { UsPersonalStep } from "./steps/us-personal-step.tsx";
import { UsContactStep } from "./steps/us-contact-step.tsx";
import { UsIdentityStep } from "./steps/us-identity-step.tsx";
import { UsReviewStep } from "./steps/us-review-step.tsx";
import styles from "../../../create-employee/create-employee-wizard.module.css";

const STEPS = [{ id: "personal", label: "Personal Details" }, { id: "contact", label: "Contact Details" }, { id: "identity", label: "Identity Details" }, { id: "review", label: "Review" }];
const PERSONAL: UsPersonalValues = { firstName: "", middleName: "", lastName: "", personalEmail: "", gender: "male", bloodGroup: "", dob: "", joiningDate: "", maritalStatus: "", anniversaryDate: "" };
const CONTACT: UsContactValues = { phoneNumber: "", emergencyContactName: "", emergencyContactNumber: "", emergencyRelationship: "Mother", presentAddress: "", permanentAddress: "" };
const IDENTITY: UsIdentityValues = { ssn: "", passportNumber: "", zipCode: "" };

export function UsPreJoinWizard({ token, onSubmitted }: { token: string; onSubmitted?: () => void }) {
  const { generateOtp, validateOtp } = usePrejoinOtp();
  const { signup } = useUsPreJoinSignup();
  const verification = useVerifiedOnboarding();
  const verifiedRecipient = verification?.token === token ? verification : null;
  const [verified, setVerified] = useState(Boolean(verifiedRecipient));
  const [personal, setPersonal] = useState(() => ({ ...PERSONAL, ...(verifiedRecipient ? { personalEmail: verifiedRecipient.email, firstName: verifiedRecipient.firstName ?? "", lastName: verifiedRecipient.lastName ?? "" } : {}) }));
  const [contact, setContact] = useState(CONTACT);
  const [identity, setIdentity] = useState(IDENTITY);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const onGenerateOtp = useCallback(
    (email: string) => generateOtp(token, email),
    [generateOtp, token],
  );
  const onValidateOtp = useCallback(async (email: string, otp: string) => {
    await validateOtp(token, email, otp);
    setPersonal((value) => ({ ...value, personalEmail: email.trim() }));
    setVerified(true);
  }, [token, validateOtp]);
  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const payload = buildUsPreJoinSignupPayload(
        { ...personal, ...contact, ...identity },
        token,
      );
      await signup(payload);
      setSubmitted(true);
      onSubmitted?.();
      showSuccess("Your form was submitted successfully.");
    } catch (error) {
      showError(
        error instanceof ApiError || error instanceof Error
          ? error.message
          : "Unable to submit the form",
      );
    } finally {
      setBusy(false);
    }
  }, [contact, identity, onSubmitted, personal, signup, token]);

  if (!verified) return <><p className={`body-sm ${styles.verifyHint}`}>Verify your personal email in the dialog to continue.</p><VerifyEmailOtpModal open variant="personal" token={token} onGenerateOtp={onGenerateOtp} onValidate={onValidateOtp} /></>;
  if (submitted) return <Card variant="subtle" padding="5xl" className={styles.card}><div className={styles.successMark}><CircleCheck size={88} strokeWidth={1.35} /></div><h2 className="heading-lg text-center">Thank you!</h2><p className={`body-sm ${styles.lead} text-center`}>Your information has been saved successfully.</p></Card>;
  const last = step === 3;
  return <div className={styles.wrap}><WizardRailLayout steps={STEPS} currentStepIndex={step} sectionTitle={STEPS[step]?.label ?? "Review"} onBack={() => setStep((value) => Math.max(0, value - 1))} primaryLabel={last ? "Submit" : "Continue"} primaryLoading={busy} primaryFormId={last ? undefined : `us-pre-step-${step}`} onPrimary={last ? () => void submit() : undefined}>
    {step === 0 && <UsPersonalStep formId="us-pre-step-0" values={personal} loading={busy} onNext={(values) => { setPersonal(values); setStep(1); }} />}
    {step === 1 && <UsContactStep formId="us-pre-step-1" values={contact} loading={busy} onNext={(values) => { setContact(values); setStep(2); }} />}
    {step === 2 && <UsIdentityStep formId="us-pre-step-2" values={identity} loading={busy} onNext={(values) => { setIdentity(values); setStep(3); }} />}
    {step === 3 && <UsReviewStep personal={personal} contact={contact} identity={identity} />}
  </WizardRailLayout></div>;
}
